// 구독 게이트 — locked 시 /billing/locked로, read_only는 컨텍스트로 노출.
// 서버(미들웨어)가 실제 권위. 프론트는 UX 반영 + 잠금 화면 유도.

import { createContext, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useAuthStore } from "@/stores/authStore";
import type { SubscriptionStatus } from "@/types/billing";

interface GateValue {
  status: SubscriptionStatus | "loading";
  readOnly: boolean;
  locked: boolean;
}

const SubscriptionGateContext = createContext<GateValue>({
  status: "loading",
  readOnly: false,
  locked: false,
});

export const useSubscriptionGate = () => useContext(SubscriptionGateContext);

export default function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const { data, isLoading } = useSubscriptionStatus();

  // super_admin은 게이팅 대상 아님
  if (user?.role === "super_admin") {
    return (
      <SubscriptionGateContext.Provider value={{ status: "none", readOnly: false, locked: false }}>
        {children}
      </SubscriptionGateContext.Provider>
    );
  }

  const status = (isLoading ? "loading" : data?.status ?? "none") as GateValue["status"];
  const enforcing = data?.enforcement_enabled ?? false;
  const locked = enforcing && (status === "locked" || status === "canceled");
  const readOnly = enforcing && status === "read_only";

  // 잠금: billing 경로 외 접근 차단 → 잠금 화면으로
  if (locked && !location.pathname.startsWith("/billing")) {
    return <Navigate to="/billing/locked" replace />;
  }

  return (
    <SubscriptionGateContext.Provider value={{ status, readOnly, locked }}>
      {children}
    </SubscriptionGateContext.Provider>
  );
}
